import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from './auth-store';

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4000/api';

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Send HTTP-only cookies
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor: auto-refresh access token on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Don't retry login/register/refresh endpoints
    const isAuthEndpoint = original.url?.includes('/auth/login') || 
                          original.url?.includes('/auth/register') || 
                          original.url?.includes('/auth/refresh');

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !isAuthEndpoint
    ) {
      original._retry = true;

      if (isRefreshing) {
        // Queue the request until refresh completes
        return new Promise((resolve) => {
          refreshQueue.push((token: string) => {
            if (original.headers) {
              original.headers['Authorization'] = `Bearer ${token}`;
            }
            resolve(api(original));
          });
        });
      }

      isRefreshing = true;

      try {
        const { data } = await api.post<{ data: { accessToken: string } }>('/auth/refresh');
        const newToken = data.data.accessToken;

        // Retry all queued requests
        refreshQueue.forEach((cb) => cb(newToken));
        refreshQueue = [];

        if (original.headers) {
          original.headers['Authorization'] = `Bearer ${newToken}`;
        }

        return api(original);
      } catch {
        // Refresh failed - clean up local session and let the caller handle it
        refreshQueue = [];
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// Typed API methods
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  register: (data: { email: string; password: string; firstName: string; lastName: string; locale?: string }) =>
    api.post('/auth/register', data),
};

export const projectsApi = {
  list: () => api.get('/projects'),
  get: (id: string) => api.get(`/projects/${id}`),
  create: (data: { name: string; description?: string; responsabile?: string }) =>
    api.post('/projects', data),
  update: (id: string, data: { name?: string; description?: string; responsabile?: string }) =>
    api.put(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  getReportSettings: (id: string) => api.get(`/projects/${id}/report-settings`),
  updateReportSettings: (id: string, data: { modelId?: string | null; primaryColor?: string; secondaryColor?: string }) =>
    api.put(`/projects/${id}/report-settings`, data),
};

export const pdfsApi = {
  list: (projectId: string) => api.get(`/projects/${projectId}/pdfs`),
  get: (projectId: string, id: string) => api.get(`/projects/${projectId}/pdfs/${id}`),
  upload: (projectId: string, file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/projects/${projectId}/pdfs`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    });
  },
  streamUrl: (projectId: string, id: string) =>
    `${API_BASE}/projects/${projectId}/pdfs/${id}/stream`,
  delete: (projectId: string, id: string) =>
    api.delete(`/projects/${projectId}/pdfs/${id}`),
};

export const markersApi = {
  list: (pdfId: string) => api.get(`/pdfs/${pdfId}/markers`),
  get: (pdfId: string, id: string) => api.get(`/pdfs/${pdfId}/markers/${id}`),
  create: (pdfId: string, data: Record<string, unknown>) =>
    api.post(`/pdfs/${pdfId}/markers`, data),
  update: (pdfId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/pdfs/${pdfId}/markers/${id}`, data),
  delete: (pdfId: string, id: string) => api.delete(`/pdfs/${pdfId}/markers/${id}`),
  addPhoto: (pdfId: string, markerId: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return api.post(`/pdfs/${pdfId}/markers/${markerId}/photos`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  photoUrl: (pdfId: string, markerId: string, photoId: string) =>
    `${API_BASE}/pdfs/${pdfId}/markers/${markerId}/photos/${photoId}`,
  deletePhoto: (pdfId: string, markerId: string, photoId: string) =>
    api.delete(`/pdfs/${pdfId}/markers/${markerId}/photos/${photoId}`),
};

export const attributesApi = {
  list: () => api.get('/attributes'),
  create: (data: { code: string; labelIt: string; labelEn?: string; description?: string }) =>
    api.post('/attributes', data),
};

export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data: { primaryColor?: string; secondaryColor?: string; defaultModelId?: string | null }) =>
    api.put('/settings', data),
  uploadModel: (file: File, name: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('name', name);
    return api.post('/settings/models', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listModels: () => api.get('/settings/models'),
  deleteModel: (id: string) => api.delete(`/settings/models/${id}`),
};

export const reportsApi = {
  downloadUrl: (projectId: string) => `${API_BASE}/projects/${projectId}/report`,
};

export default api;
